import type { ReactNode } from 'react';
import { FigApiError } from '../core/api/errors.js';

/**
 * Empty, error and offline states.
 *
 * THE-BAR is explicit that these are where a product stops feeling cheap, and that they are
 * the screens most often left until last. Three rules they all follow:
 *   - say something useful, never "No results"
 *   - human words, never API language
 *   - always a way forward
 */

export function EmptyState({ title, body, action }: { title: string; body: string; action?: ReactNode }) {
  return (
    <div className="state" role="status">
      <h2 className="state-title">{title}</h2>
      <p className="state-body">{body}</p>
      {action}
    </div>
  );
}

export function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  // FigApiError already carries a human message - the server's own `detail` when it gave one,
  // and copy that names no cause it does not know when it did not.
  const message = error instanceof FigApiError
    ? error.message
    : error instanceof Error && error.message
      ? error.message
      : 'Something went wrong. Try again.';
  const transient = error instanceof FigApiError ? error.isTransient : true;
  return (
    <div className="state state-error" role="alert">
      <h2 className="state-title">That didn’t load</h2>
      <p className="state-body">{message}</p>
      {onRetry && (
        <button className="btn" onClick={onRetry}>
          {transient ? 'Try again' : 'Reload'}
        </button>
      )}
    </div>
  );
}

export function OfflineNotice({ lastUpdated }: { lastUpdated?: Date | null }) {
  return (
    <div className="banner banner-offline" role="status" aria-live="polite">
      <strong>You’re offline.</strong>{' '}
      {lastUpdated
        ? `Showing calls saved on this device, last updated ${lastUpdated.toLocaleString()}.`
        : 'Calls you’ve already opened are still readable.'}
    </div>
  );
}

/** A skeleton shaped like the rows it replaces, so the list does not jump when data lands. */
export function RowSkeleton({ count = 8 }: { count?: number }) {
  return (
    <ul className="rows" aria-hidden="true">
      {Array.from({ length: count }, (_, i) => (
        <li className="row row-skeleton" key={i}>
          <span className="sk sk-avatar" />
          <span className="sk-stack">
            <span className="sk sk-line sk-title" />
            <span className="sk sk-line sk-meta" />
          </span>
          <span className="sk sk-dur" />
        </li>
      ))}
    </ul>
  );
}
